import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import ru from "./ru.json";
import en from "./en.json";

export type Locale = "ru" | "en";
type Dict = Record<string, string>;
const DICTS: Record<Locale, Dict> = { ru: ru as Dict, en: en as Dict };
const STORAGE_KEY = "forma.locale";

function initialLocale(): Locale {
  try {
    return localStorage.getItem(STORAGE_KEY) === "en" ? "en" : "ru";
  } catch {
    return "ru"; // Russian is the default
  }
}

export function translate(locale: Locale, key: string, params?: Record<string, string | number>): string {
  const template = DICTS[locale][key] ?? DICTS.ru[key] ?? key;
  return params ? template.replace(/\{(\w+)\}/g, (_, name: string) => String(params[name] ?? `{${name}}`)) : template;
}

interface I18n {
  locale: Locale;
  setLocale(locale: Locale): void;
  t(key: string, params?: Record<string, string | number>): string;
  formatDate(value: string | Date | null | undefined, withTime?: boolean): string;
  formatMoney(amountMinor: string, currency: string): string;
  errorText(code: string | undefined): string;
}

const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode: keep in memory */
    }
  }, []);
  const value = useMemo<I18n>(() => {
    const tag = locale === "ru" ? "ru-RU" : "en-GB";
    return {
      locale,
      setLocale,
      t: (key, params) => translate(locale, key, params),
      formatDate: (v, withTime = false) => {
        if (!v) return "—";
        const d = typeof v === "string" ? new Date(v) : v;
        return new Intl.DateTimeFormat(tag, withTime ? { dateStyle: "medium", timeStyle: "short" } : { dateStyle: "long" }).format(d);
      },
      formatMoney: (amountMinor, currency) => {
        const digits = 2;
        const s = amountMinor.padStart(digits + 1, "0");
        const decimal = `${s.slice(0, -digits)}.${s.slice(-digits)}`;
        // Intl accepts decimal strings: no floating point rounding on amounts.
        return new Intl.NumberFormat(tag, { style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(decimal as unknown as number);
      },
      errorText: (code) => {
        const key = `errors.${code ?? "UNKNOWN"}`;
        return DICTS[locale][key] ? translate(locale, key) : translate(locale, "errors.UNKNOWN");
      },
    };
  }, [locale, setLocale]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error("I18nProvider missing");
  return v;
}
